import { useEffect, useRef } from "react";

const N = 240;
const LINK = 0.33;

const buildSphere = () => {
  const pts = [];
  const g = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < N; i++) {
    const y = 1 - (i / (N - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    pts.push([Math.cos(g * i) * r, y, Math.sin(g * i) * r]);
  }
  const pairs = [];
  for (let a = 0; a < N; a++) for (let b = a + 1; b < N; b++) {
    const d = Math.hypot(pts[a][0] - pts[b][0], pts[a][1] - pts[b][1], pts[a][2] - pts[b][2]);
    if (d < LINK) pairs.push([a, b]);
  }
  return { pts, pairs };
};

export const HeroCanvas = ({ className = "" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    const { pts, pairs } = buildSphere();
    const proj = new Array(N);
    let w = 0, h = 0, raf, ry = 0, rx = -0.35, mx = 0, my = 0, t = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = c.clientWidth; h = c.clientHeight;
      c.width = w * dpr; c.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const onMove = (e) => {
      mx = (e.clientX / window.innerWidth - 0.5) * 2;
      my = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    const draw = () => {
      t += 0.016;
      ry += 0.0022 + mx * 0.004;
      rx += (-0.35 + my * 0.35 - rx) * 0.04;
      const R = Math.min(w, h) * 0.38, cx = w / 2, cy = h / 2, f = 2.6;
      const cyR = Math.cos(ry), syR = Math.sin(ry), cxR = Math.cos(rx), sxR = Math.sin(rx);
      for (let i = 0; i < N; i++) {
        const [x0, y0, z0] = pts[i];
        const pulse = 1 + Math.sin(t * 1.4 + i * 0.37) * 0.025;
        const x1 = x0 * cyR - z0 * syR, z1 = x0 * syR + z0 * cyR;
        const y2 = y0 * cxR - z1 * sxR, z2 = y0 * sxR + z1 * cxR;
        const s = f / (f + z2);
        proj[i] = [cx + x1 * R * s * pulse, cy + y2 * R * s * pulse, z2];
      }
      ctx.clearRect(0, 0, w, h);
      ctx.lineWidth = 0.7;
      for (let k = 0; k < pairs.length; k++) {
        const a = proj[pairs[k][0]], b = proj[pairs[k][1]];
        const depth = 1 - ((a[2] + b[2]) / 2 + 1) / 2;
        ctx.strokeStyle = `rgba(167,139,250,${0.04 + depth * 0.28})`;
        ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
      }
      for (let i = 0; i < N; i++) {
        const p = proj[i];
        const depth = 1 - (p[2] + 1) / 2;
        const hot = i % 37 === 0;
        ctx.fillStyle = hot ? `rgba(237,233,254,${0.5 + depth * 0.5})` : `rgba(${129 + depth * 60},${140 + depth * 40},248,${0.25 + depth * 0.7})`;
        ctx.beginPath(); ctx.arc(p[0], p[1], (hot ? 2.6 : 1.3) + depth * 1.4, 0, Math.PI * 2); ctx.fill();
      }
      const orbit = t * 0.5;
      ctx.strokeStyle = "rgba(129,140,248,0.22)";
      ctx.beginPath(); ctx.ellipse(cx, cy, R * 1.32, R * 0.36, -0.42, 0, Math.PI * 2); ctx.stroke();
      for (let k = 0; k < 3; k++) {
        const a = orbit + (k * Math.PI * 2) / 3;
        const ox = Math.cos(a) * R * 1.32, oy = Math.sin(a) * R * 0.36;
        const x = cx + ox * Math.cos(-0.42) - oy * Math.sin(-0.42);
        const y = cy + ox * Math.sin(-0.42) + oy * Math.cos(-0.42);
        ctx.fillStyle = "rgba(196,181,253,0.95)";
        ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(c);
    window.addEventListener("pointermove", onMove, { passive: true });
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); window.removeEventListener("pointermove", onMove); };
  }, []);

  return <canvas ref={ref} data-testid="hero-canvas" className={`h-full w-full ${className}`} aria-hidden="true" />;
};
